/*
 * <listicle-offer> - the listicle page's buy box. Markup and first paint:
 * sections/listicle-offer.liquid, which also renders every number below once.
 *
 * One flavour, one plan, a quantity. Prices are checkout's own maths, as in
 * assets/buy-box.js:
 *
 *   list    = qty x variant price                              (one-time)
 *   plan    = qty x the bag's price on the SKIO plan           (Shopify rounds per unit)
 *   renewal = round(plan x (100 - volume) / 100)               (Skio's volume %, picked
 *                                                               by the plan subtotal)
 *   first   = plan - floor(plan x welcome % / 100)             (the welcome code is an
 *                                                               order discount, and
 *                                                               Shopify rounds it down)
 *
 * Without a welcome code the first order is the renewal price.
 *
 * Add to cart: subscribing puts the welcome code on the cart first (codes already
 * there are kept - /cart/update.js replaces the whole list), then one /cart/add.js
 * with sections, rendered by the cart drawer exactly like Dawn's own add.
 * cart-gift.js watches cart writes and adds the free Creatine for the code.
 */

if (!customElements.get('listicle-offer')) {
  class ListicleOffer extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      const dataEl = this.querySelector('[data-lo-data]');
      if (!dataEl) return;
      try {
        this.data = JSON.parse(dataEl.textContent);
      } catch (error) {
        console.error('listicle-offer: bad data', error);
        return;
      }
      this.ready = true;

      const checkedFlavor = this.querySelector('[data-lo-flavor]:checked');
      const checkedPlan = this.querySelector('[data-lo-plan]:checked');
      this.flavor = this.find(checkedFlavor ? checkedFlavor.value : '') || this.data.flavors.find((f) => f.available) || this.data.flavors[0];
      this.plan = checkedPlan ? checkedPlan.value : 'once';
      this.qty = 1;
      this.form = this.querySelector('form');

      this.addEventListener('change', (event) => this.onChange(event));
      this.addEventListener('click', (event) => this.onClick(event));
      if (this.form) {
        this.form.addEventListener('submit', (event) => {
          event.preventDefault();
          this.addToCart();
        });
      }
      this.render();
    }

    find(name) {
      return this.data.flavors.find((f) => f.name === name);
    }

    onChange(event) {
      const input = event.target;
      if (input.matches('[data-lo-flavor]')) {
        const flavor = this.find(input.value);
        if (flavor && flavor.available) this.flavor = flavor;
      } else if (input.matches('[data-lo-plan]')) {
        this.plan = input.value === 'sub' ? 'sub' : 'once';
      } else {
        return;
      }
      this.render();
    }

    onClick(event) {
      const step = event.target.closest('[data-lo-step]');
      if (step && !step.disabled) {
        const max = Math.max(Number(this.data.max) || 1, 1);
        this.qty = Math.min(Math.max(this.qty + Number(step.dataset.loStep), 1), max);
        this.render();
        return;
      }
      /* The value stack sits outside the radio's label; a press on it still means "subscribe". */
      if (event.target.closest('[data-lo-stack]') && this.plan !== 'sub') {
        const sub = this.querySelector('[data-lo-plan][value="sub"]');
        if (sub) {
          sub.checked = true;
          this.plan = 'sub';
          this.render();
        }
      }
    }

    /* Maths --------------------------------------------------------------- */

    totals() {
      const d = this.data;
      const f = this.flavor;
      const q = this.qty;
      const list = f.price * q;
      const hasPlan = Boolean(d.planId) && f.plan != null;
      const plan = hasPlan ? f.plan * q : list;

      let volume = 0;
      (d.triggers || []).forEach((trigger, i) => {
        if (plan >= trigger) volume = (d.volume || [])[i] || 0;
      });
      const renewal = Math.round((plan * (100 - volume)) / 100);
      const first = d.welcomeCode ? plan - Math.floor((plan * (d.welcomePct || 0)) / 100 + 1e-9) : renewal;
      /* What the first order takes off the one-time price, whole %, never rounded up. */
      const pct = list > 0 ? Math.floor(((list - first) * 100) / list + 1e-9) : 0;
      const servings = f.servings * q;
      return { list, hasPlan, renewal, first, pct, perServing: servings > 0 ? Math.round(first / servings) : first };
    }

    /* Render -------------------------------------------------------------- */

    render() {
      const d = this.data;
      const f = this.flavor;
      const t = this.totals();
      if (!t.hasPlan) this.plan = 'once';
      const isSub = this.plan === 'sub';
      const pay = isSub ? t.first : t.list;
      const money = (cents) => this.money(cents);
      const fill = (template, values) =>
        String(template || '').replace(/\[(\w+)\]/g, (match, key) => (key in values ? values[key] : match));
      const set = (selector, text) => {
        const el = this.querySelector(selector);
        if (el) el.textContent = text;
      };
      const show = (selector, on) => {
        const el = this.querySelector(selector);
        if (el) el.hidden = !on;
      };

      this.querySelectorAll('[data-lo-flavor]').forEach((input) => {
        const on = input.value === f.name;
        input.checked = on;
        input.closest('.lo-tile').classList.toggle('is-selected', on);
      });
      this.querySelectorAll('[data-lo-plan]').forEach((input) => {
        input.checked = input.value === this.plan;
      });
      this.querySelectorAll('[data-lo-opt]').forEach((opt) => {
        opt.classList.toggle('is-selected', opt.dataset.loOpt === this.plan);
      });

      set('[data-lo-selected]', f.name);
      set('[data-lo-servings]', fill(d.text.servings, { servings: f.servings }));

      const discounted = t.first < t.list;
      set('[data-lo-sub-was]', money(t.list));
      show('[data-lo-sub-was]', discounted);
      set('[data-lo-sub-now]', money(t.first));

      const rowTitle = fill(d.text.rowTitle, { servings: f.servings, product: d.product });
      set('[data-lo-row-title]', this.qty > 1 ? this.qty + ' × ' + rowTitle : rowTitle);
      set('[data-lo-row-text]', fill(d.text.rowText, { flavor: f.name }));
      set('[data-lo-row-was]', money(t.list));
      show('[data-lo-row-was]', discounted);
      set('[data-lo-row-now]', money(t.first));
      set('[data-lo-total-title]', fill(d.text.totalTitle, { pct: t.pct }));
      set('[data-lo-per]', fill(d.text.perServing, { price: money(t.perServing) }));
      set('[data-lo-total]', money(t.first));
      set('[data-lo-once-price]', money(t.list));

      set('[data-lo-qty]', String(this.qty));
      const minus = this.querySelector('[data-lo-step="-1"]');
      const plus = this.querySelector('[data-lo-step="1"]');
      if (minus) minus.disabled = this.qty <= 1;
      if (plus) plus.disabled = this.qty >= (Number(d.max) || 1);

      const cta = this.querySelector('[data-lo-cta]');
      if (cta) cta.disabled = !f.available;
      set('[data-lo-cta-label]', f.available ? fill(d.text.cta, { price: money(pay) }) : d.text.soldOut);

      const note = isSub ? fill(d.text.noteSub, { renewal: money(t.renewal), days: d.days }) : d.text.noteOnce || '';
      set('[data-lo-note]', note);
      show('[data-lo-note]', note !== '');

      /* The no-JS form fields, kept true so the fallback post matches the box. */
      const idInput = this.querySelector('[data-lo-id]');
      const qtyInput = this.querySelector('[data-lo-qty-input]');
      const planInput = this.querySelector('[data-lo-plan-input]');
      if (idInput) idInput.value = f.id;
      if (qtyInput) qtyInput.value = this.qty;
      if (planInput) {
        planInput.value = isSub ? d.planId : '';
        planInput.disabled = !isSub;
      }
    }

    /* Add to cart --------------------------------------------------------- */

    async addToCart() {
      const button = this.querySelector('[data-lo-cta]');
      const f = this.flavor;
      if (!button || button.disabled || button.getAttribute('aria-disabled') === 'true' || !f.available) return;
      const isSub = this.plan === 'sub';

      this.showError('');
      button.setAttribute('aria-disabled', 'true');
      button.classList.add('is-loading');

      try {
        if (isSub && this.data.welcomeCode) await this.applyCode(this.data.welcomeCode);

        const item = { id: f.id, quantity: this.qty };
        if (isSub && this.data.planId) item.selling_plan = Number(this.data.planId);

        const cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
        const body = { items: [item] };
        if (cart && typeof cart.getSectionsToRender === 'function') {
          body.sections = cart.getSectionsToRender().map((section) => section.id);
          body.sections_url = window.location.pathname;
          cart.setActiveElement(document.activeElement);
        }

        const config = fetchConfig('json');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        config.body = JSON.stringify(body);
        const response = await fetch(window.routes.cart_add_url, config).then((r) => r.json());

        /* Shopify's own message, never a silent retry: a 422 is usually stock. */
        if (response.status) {
          this.showError(response.description || response.message || this.data.text.error);
          return;
        }
        if (!cart) {
          window.location = window.routes.cart_url;
          return;
        }
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'listicle-offer', cartData: response });
        }
        cart.classList.remove('is-empty');
        cart.renderContents(response);
      } catch (error) {
        console.error(error);
        this.showError(this.data.text.error);
      } finally {
        button.classList.remove('is-loading');
        button.removeAttribute('aria-disabled');
      }
    }

    /* Adds the code to whatever codes the cart already holds. */
    async applyCode(code) {
      const json = { headers: { 'Content-Type': 'application/json', Accept: 'application/json' } };
      const cartUrl = (window.routes && window.routes.cart_url) || '/cart';
      const cart = await fetch(cartUrl + '.js', json).then((r) => r.json());
      const codes = (cart.discount_codes || []).map((entry) => String(entry.code || '')).filter(Boolean);
      if (codes.some((entry) => entry.toUpperCase() === String(code).toUpperCase())) return;
      codes.push(code);
      const updateUrl = (window.routes && window.routes.cart_update_url) || '/cart/update';
      await fetch(updateUrl + '.js', Object.assign({ method: 'POST', body: JSON.stringify({ discount: codes.join(',') }) }, json));
    }

    showError(message) {
      const el = this.querySelector('[data-lo-error]');
      if (!el) return;
      el.textContent = message || '';
      el.hidden = !message;
    }

    /* Money: the shop's own format, as Liquid's `| money` would print it. */
    money(cents) {
      const format = this.dataset.moneyFormat || '${{amount}}';
      const token = format.match(/\{\{\s*(\w+)\s*\}\}/);
      const value = Number(cents) || 0;
      const group = (places, thousands, decimal) => {
        const [whole, fraction] = (value / 100).toFixed(places).split('.');
        return whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands) + (fraction ? decimal + fraction : '');
      };
      if (!token) return group(2, ',', '.');
      const styles = {
        amount_no_decimals: [0, ',', '.'],
        amount_with_comma_separator: [2, '.', ','],
        amount_no_decimals_with_comma_separator: [0, '.', ','],
        amount_with_apostrophe_separator: [2, "'", '.'],
        amount_with_space_separator: [2, ' ', ','],
      };
      const [places, thousands, decimal] = styles[token[1]] || [2, ',', '.'];
      return format.replace(token[0], group(places, thousands, decimal));
    }
  }

  customElements.define('listicle-offer', ListicleOffer);
}
